/**
 * Campo per gli importi, che capisce il formato italiano.
 *
 * Un <input type="number"> legge il punto come separatore DECIMALE: chi scrive
 * "3.380" intendendo tremilatrecentottanta si ritrova 3,38 in archivio, e la
 * virgola non è nemmeno accettata. È successo davvero sull'offerta FB2526.
 *
 * Qui il campo è di testo e la lettura segue le convenzioni italiane; alla
 * conferma il valore si riscrive formattato, così si vede subito cosa ha capito
 * il sistema. Verso l'esterno resta un numero puro.
 */
import { useEffect, useState } from 'react';

/**
 * Interpreta un importo scritto a mano.
 *
 *   "3.380"      → 3380     punto seguito da tre cifre: separatore di migliaia
 *   "3.380,50"   → 3380.5   punto migliaia, virgola decimali
 *   "3380,50"    → 3380.5
 *   "0.50"       → 0.5      due cifre dopo il punto: decimali
 *   "1.234.567"  → 1234567
 */
export function leggiImporto(valore) {
  if (valore === null || valore === undefined) return null;
  let t = String(valore).replace(/[€\s ]/g, '').trim();
  if (!t) return null;

  const negativo = t.startsWith('-');
  t = t.replace(/^[-+]/, '');

  if (t.includes(',')) {
    // La virgola è sempre decimale: i punti che restano sono migliaia
    t = t.replace(/\./g, '').replace(',', '.');
  } else {
    const punti = t.split('.').length - 1;
    if (punti === 1) {
      const [, dopo] = t.split('.');
      // Tre cifre dopo il punto = migliaia ("3.380"); altrimenti decimali ("0.50")
      if (dopo.length === 3) t = t.replace('.', '');
    } else if (punti > 1) {
      t = t.replace(/\./g, '');          // "1.234.567"
    }
  }

  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return negativo ? -n : n;
}

const formatta = (n) =>
  n === null || n === undefined || n === ''
    ? ''
    : new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(Number(n));

export default function CampoImporto({ value, onChange, className = 'input', ...resto }) {
  const [testo, setTesto] = useState(() => formatta(value));
  const [scrivendo, setScrivendo] = useState(false);

  // Mentre si digita non si riscrive il campo sotto le dita: si riallinea solo
  // quando il valore cambia da fuori (ricarica, precompilazione).
  useEffect(() => {
    if (!scrivendo) setTesto(formatta(value));
  }, [value]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <input
      {...resto}
      type="text"
      inputMode="decimal"
      className={className}
      value={testo}
      onFocus={(e) => { setScrivendo(true); e.target.select(); }}
      onChange={(e) => {
        setTesto(e.target.value);
        const n = leggiImporto(e.target.value);
        onChange(n === null ? '' : n);
      }}
      onBlur={() => {
        setScrivendo(false);
        const n = leggiImporto(testo);
        setTesto(n === null ? '' : formatta(n));   // mostra cosa ha capito
        onChange(n === null ? '' : n);
      }}
    />
  );
}
